export type ApplicationStatus =
  | "draft"
  | "guardian_pending"
  | "pending_review"
  | "invited"
  | "waitlisted"
  | "declined"
  | "accepted"
  | "rejected";

export type MemberRole = "connector" | "deal_operator" | "reviewer" | "admin";

export type MiddlemanRoute =
  | "clipper"
  | "seeder"
  | "closer"
  | "scout"
  | "promoter"
  | "unplaced";

export type MissionOutcome = "views" | "conversions" | "demo_calls" | "pipeline";
export type ReviewDecision = "approved" | "rejected";

export interface ApplicationDraft {
  name: string;
  country: string;
  timezone: string;
  dateOfBirth: string;
  primaryRoute: MiddlemanRoute;
  backupRoute: MiddlemanRoute;
  cityCountry: string;
  instagramHandle: string;
  whatsapp: string;
  currentStatus: string;
  workPreference: string;
  dealExperience: string;
  commissionExperience: string;
  obsession: string;
  outworkedMoment: string;
  rejectionResponse: string;
  quitReason: string;
  loyaltyUnderPressure: string;
  canSurviveNoFixedIncome: string;
  firstThirtyDaysPlan: string;
  videoLink: string;
  availabilityBand: string;
  languages: string;
  experience: string;
  whyEntry: string;
  portfolioUrl: string;
  tools: string;
  sampleVersion: string;
  sampleResponse: string;
  guardianName: string;
  guardianEmail: string;
  guardianRelationship: string;
  confirmsAgeAndTraining: boolean;
  confirmsCodeOfConduct: boolean;
  confirmsPrivacy: boolean;
  confirmsAccuracy: boolean;
}

export interface ReviewStatus {
  status: ApplicationStatus;
  submittedAt: string;
  reapplyAt?: string | null;
  guardianRequired: boolean;
  guardianApproved: boolean;
  nextStep: string;
}

export interface Opportunity {
  id: string;
  twentyId: string;
  brand: string;
  market: string;
  signal: string;
  aiScore: number;
  expectedInvoice: number;
  currency: string;
  publicSummary: string;
  status: "open" | "claimed" | "closed";
  routeRequired?: MiddlemanRoute;
  outcome?: MissionOutcome;
  deliverable?: string;
  bountyMin?: number;
  bountyMax?: number;
  highValue?: boolean;
}

export interface ActiveClaim {
  id: string;
  opportunityId: string;
  expiresAt: string;
  opportunity?: Opportunity;
}

export interface MissionReview {
  id: string;
  claimId: string;
  brand: string;
  evidenceNote: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  sopChecklist: string[];
  honeypot: boolean;
  expectedDecision?: ReviewDecision;
}

export interface XpLogEntry {
  id: string;
  action: string;
  amount: number;
  createdAt: string;
}

export interface ProvingSubmission {
  step: 1 | 2 | 3;
  title: string;
  status: "locked" | "ready" | "submitted" | "approved";
  instructions: string;
  submission?: string;
}

export interface LeaderboardEntry {
  id: string;
  handle: string;
  route: MiddlemanRoute;
  weeklyXp: number;
  approvedMissions: number;
  trustScore: number;
  position: number;
}

export interface ClaimChecklist {
  confirmedIdentity: boolean;
  reviewedContext: boolean;
  preparedQuestions: boolean;
  confirmedTiming: boolean;
  acceptedTerms: boolean;
}

export interface LedgerEntry {
  id: string;
  source: string;
  amount: number;
  currency: string;
  rate: number;
  status: "pending_invoice" | "hold" | "approved" | "paid" | "reversed";
  createdAt: string;
  externalReference?: string | null;
}

export interface CircleMember {
  id: string;
  handle: string;
  rank: string;
  verifiedResults: number;
  status: string;
}

export interface MemberNotification {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
}

export interface MemberProfile {
  id: string;
  handle: string;
  role: MemberRole;
  activeRoute: MiddlemanRoute;
  xp: number;
  level: number;
  trustScore: number;
  sponsorId?: string | null;
  rank: string;
  verifiedResults: number;
  minorAccessApproved: boolean;
  membershipStatus?: "pending" | "active" | "suspended" | "closed";
  legalApproved?: boolean;
  payoutEligible?: boolean;
  discordUsername?: string;
}

export const emptyApplication: ApplicationDraft = {
  name: "",
  country: "",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  dateOfBirth: "",
  primaryRoute: "unplaced",
  backupRoute: "unplaced",
  cityCountry: "",
  instagramHandle: "",
  whatsapp: "",
  currentStatus: "",
  workPreference: "",
  dealExperience: "",
  commissionExperience: "",
  obsession: "",
  outworkedMoment: "",
  rejectionResponse: "",
  quitReason: "",
  loyaltyUnderPressure: "",
  canSurviveNoFixedIncome: "",
  firstThirtyDaysPlan: "",
  videoLink: "",
  availabilityBand: "",
  languages: "English",
  experience: "",
  whyEntry: "",
  portfolioUrl: "",
  tools: "",
  sampleVersion: "",
  sampleResponse: "",
  guardianName: "",
  guardianEmail: "",
  guardianRelationship: "",
  confirmsAgeAndTraining: false,
  confirmsCodeOfConduct: false,
  confirmsPrivacy: false,
  confirmsAccuracy: false,
};
